/* eslint-disable camelcase */
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/react-hooks';
import {
  usePlaidLink,
  PlaidLinkOnSuccess,
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOptions,
  PlaidLinkStableEvent,
  PlaidLinkOnEventMetadata,
} from 'react-plaid-link';

import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import {
  AGGREGATORS,
  ConnectionStatus,
  DUTCHIE_PAY_QUERY_PARMAS_KEY,
  editBankAccount,
  EnrollmentSteps,
  PLAID_OAUTH_PATH,
  PLAID_STORAGE_KEY,
  REDIRECT_URL,
} from 'src/payments/constants';
import {
  ENROLLMENT_EVENTS,
  EVENT_ACTIONS,
  useDutchiePayAnalytics,
} from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';
import { useStores } from 'contexts/stores';
import completeAggregatorLink from 'src/payments/graphql/mutations/complete-aggretator-link.gql';
import { useStartAggregatorLinkQuery } from 'types/graphql';
import { parsePlaidMetaData } from 'src/payments/utils/parse-meta-data';
import { useDutchiePay } from 'src/payments/hooks';
import useUI from 'hooks/use-ui';
import useViewport from 'hooks/use-viewport';
import { useObserver } from 'mobx-react-lite';
import { getSessionId } from 'src/utils/analytics/clients/sardine-client';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';

export function AggregatorWidget({ relinkBankAccountEnabled }): JSX.Element {
  const apolloClient = useApolloClient();
  const { completeAggregatorLinkSucceeded, plaidLinkExited, completeAggregatorLinkStarted } = useDutchiePayEnrollment();
  const { dutchiePayEventTracker, trackPlaidOnEvent } = useDutchiePayAnalytics();
  const { User } = useStores();
  const { paymentMethodId, aggregator, connectionStatus, requiresAggregatorMigration } = useDutchiePay();
  const { asPath, query } = useRouter();
  const router = useRouter();
  const isOAuthRedirect = asPath.includes(PLAID_OAUTH_PATH);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const showErnie = useErnie();
  const linkedWithMx = aggregator === AGGREGATORS.MX;
  const queryPramas = localStorage.getItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
  const { embeddedEventDispatcher } = useUI();
  const viewport = useViewport();
  const editBankAccountEnabled = router.query.step === editBankAccount;
  const userFullName = useObserver(() => User.fullName);

  const viewportHeight = viewport.height;
  const usePaymentMethodId =
    paymentMethodId && !editBankAccountEnabled && !linkedWithMx && connectionStatus !== ConnectionStatus.revoked;

  const { data: startAggregatorLinkData, loading: startAggregatorLinkLoading } = useStartAggregatorLinkQuery({
    variables: {
      aggregator: AGGREGATORS.PLAID,
      redirectUrl: REDIRECT_URL,
      entityName: userFullName,
      paymentMethodId: usePaymentMethodId ? paymentMethodId : '',
    },
    skip: (relinkBankAccountEnabled && !paymentMethodId && !requiresAggregatorMigration) || !userFullName,
    fetchPolicy: 'network-only',
  });

  const persistLinkData = !token && !redirectUrl && !startAggregatorLinkLoading && startAggregatorLinkData;
  const plaidLinkToken = startAggregatorLinkData?.startAggregatorLink.plaid.linkToken ?? '';

  const clearPlaidData = (): void => {
    localStorage.removeItem(DUTCHIE_PAY_QUERY_PARMAS_KEY);
    localStorage.removeItem(PLAID_STORAGE_KEY);
    setRedirectUrl('');
  };

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken, metadata) => {
      completeAggregatorLinkStarted();
      const formattedMetaData = parsePlaidMetaData({ metadata });
      try {
        const response = await apolloClient.mutate({
          mutation: completeAggregatorLink,
          variables: {
            aggregatorToken: publicToken,
            aggregator: AGGREGATORS.PLAID,
            plaid: { ...formattedMetaData },
            paymentMethodId: usePaymentMethodId ? paymentMethodId : '',
            sessionId: getSessionId(),
          },
        });

        const pendingPaymentMethod = response.data.completeAggregatorLink.paymentMethods.filter(
          (method) => method.status === 'pending'
        );

        const bankDetails = {
          lastDigits: metadata.accounts[0].mask,
          bankName: metadata.institution?.name,
          id: pendingPaymentMethod[0].id,
          ownerName: User.fullName,
          accountName: metadata.accounts[0].name,
        };

        if (pendingPaymentMethod) {
          completeAggregatorLinkSucceeded(bankDetails);
        }

        dutchiePayEventTracker(ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
          step: EnrollmentSteps.connectBank,
          action: EVENT_ACTIONS.SUCCEEDED,
        });

        if (isOAuthRedirect && queryPramas) {
          void router.replace({
            query: { ...router.query, ...JSON.parse(queryPramas) },
          });
        }
      } catch (err) {
        console.error(err);
        showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.MED);
        plaidLinkExited();
      } finally {
        clearPlaidData();
      }
    },
    [
      dutchiePayEventTracker,
      User.fullName,
      apolloClient,
      completeAggregatorLinkSucceeded,
      completeAggregatorLinkStarted,
      isOAuthRedirect,
      plaidLinkExited,
      queryPramas,
      router,
      showErnie,
      usePaymentMethodId,
      paymentMethodId,
    ]
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName: PlaidLinkStableEvent, metadata: PlaidLinkOnEventMetadata) => {
      trackPlaidOnEvent(eventName, metadata);
    },
    [trackPlaidOnEvent]
  );

  const onExit = useCallback<PlaidLinkOnExit>(() => {
    clearPlaidData();
    plaidLinkExited();
  }, [plaidLinkExited]);

  const config: PlaidLinkOptions = {
    token,
    onSuccess,
    onEvent,
    onExit,
  };

  if (isOAuthRedirect) {
    config.receivedRedirectUri = redirectUrl;
  }

  useEffect(() => {
    if (persistLinkData) {
      setToken(plaidLinkToken);
      // save this in localstorage in case we have an oAuth redirect
      localStorage.setItem(PLAID_STORAGE_KEY, plaidLinkToken);
    }
  }, [persistLinkData, plaidLinkToken]);

  useEffect(() => {
    if (asPath.includes(PLAID_OAUTH_PATH)) {
      setRedirectUrl(window.location.href);
      setToken(localStorage.getItem(PLAID_STORAGE_KEY));
    }
    if (asPath.includes('?') && !asPath.includes(PLAID_OAUTH_PATH)) {
      // save the query params in localstorage in case we use oAuth
      localStorage.setItem(DUTCHIE_PAY_QUERY_PARMAS_KEY, JSON.stringify(query));
    }
  }, [asPath, query]);

  useEffect(() => {
    embeddedEventDispatcher(`iframe:resize:override`, { height: viewportHeight });
    embeddedEventDispatcher(`iframe:scrollToTop`, { force: true });
  }, [embeddedEventDispatcher, viewportHeight]);

  const { open, ready } = usePlaidLink(config);
  useEffect(() => {
    if (ready) {
      open();
    }
  }, [open, ready]);

  return <></>;
}
